(function(angular){
	'use strict';

	angular
		.module('apiModule')
		.factory('ReitElementFactory', [
			'$filter',
			'$q',
			'Big',
			'INVESTMENT_ENTITY_ID',
			'PropertyService',
			'urlFactory',
			reitElementServiceFunction
		]);

	function reitElementServiceFunction(
		$filter,
		$q,
		Big,
		INVESTMENT_ENTITY_ID,
		PropertyService,
		urlFactory
	) {
		var factory = {
			getReitElement: getReitElement,
			getAllReitElements: getAllReitElements,
			getReitElementsForInvestmentEntity: getReitElementsForInvestmentEntity,
			getReitElementsForReit: getReitElementsForReit,
			getReitElementsForRiaPlan: getReitElementsForRiaPlan,
			getReitElementListsForDashboard: getReitElementListsForDashboard,
			getReitElementListsForPlan: getReitElementListsForPlan,
			getCapitalStackForElement: getCapitalStackForElement,
		};

		var formattingFunctions = {
			'weightedReitElements': function(weightedReitElements) { return weightedReitElements; },
			'weightedReitElementsByStatus': getWeightedReitElementsByStatus,
			'flatReitElementList': getFlatReitElementList,
			'flatReitElementListByStatus': getFlatReitElementListByStatus,
			'flatReitElementListWithWeights': getFlatReitElementListWithWeights,
			'flatReitElementListByStatusWithWeights': getFlatReitElementListByStatusWithWeights,
			'ratingAllocations': getReitElementsByRatingAllocation
		};

		return factory;

		function getReitElement(reitElementId) {
			var deferred = $q.defer();

			var reitElementUrl = '/api/reit-elements/' + reitElementId;

			urlFactory.apiGetPromise(reitElementUrl)
				.then(function (response) {
					deferred.resolve(PropertyService.formatPropertyListItem(response.data));
				})
				.catch(function() {
					deferred.resolve(undefined);
				});

			return deferred.promise;
		}

		function getAllReitElements() {
			var deferred = $q.defer();

			var reitElementUrl = '/api/reit-elements';

			urlFactory.apiGetPromise(reitElementUrl)
				.then(function (response) {
					var formattedReitElementList = response.data.map(function(reitElement) {
						return formatReitElement(reitElement);
					});
					var reitElementsByStatus = groupReitElementsByStatus(formattedReitElementList);
					deferred.resolve(reitElementsByStatus);
				}, function() {
					deferred.reject(undefined);
				});

			return deferred.promise;
		}

		function getReitElementsForInvestmentEntity(formats) {
			var deferred = $q.defer();

			var reitElementUrl = '/api-secured/investment-entity/' + INVESTMENT_ENTITY_ID + '/portfolio/reit-elements';

			urlFactory.apiGetPromise(reitElementUrl)
				.then(function (response) {
					deferred.resolve(getFormattedReitElementLists(response.data, formats));
				})
				.catch(function() {
					deferred.resolve(undefined);
				});

			return deferred.promise;
		}

		function getReitElementsForReit(reitId, formats) {
			var deferred = $q.defer();

			var reitElementUrl = '/api/reits/' + reitId + '/reit-elements';

			urlFactory.apiGetPromise(reitElementUrl)
				.then(function (response) {
					deferred.resolve(getFormattedReitElementLists(response.data, formats));
				})
				.catch(function() {
					deferred.resolve(undefined);
				});

			return deferred.promise;
		}

		function getReitElementsForRiaPlan(riaPlanId, formats) {
			var deferred = $q.defer();

			var reitElementUrl = '/api/ria/' + riaPlanId + '/reit-elements';

			urlFactory.apiGetPromise(reitElementUrl)
				.then(function (response) {
					deferred.resolve(getFormattedReitElementLists(response.data, formats));
				}, function() {
					deferred.reject(undefined);
				});

			return deferred.promise;
		}

		function getCapitalStackForElement(reitElementId) {
			var capitalStackUrl = '/api/reit-elements/' + reitElementId + '/capital-stack';

			return urlFactory.apiGetPromise(capitalStackUrl)
				.then(function(response) {
					return response.data;
				});
		}

		function getReitElementListsForDashboard() {
			var dashboardFormatTypes = ['weightedReitElementsByStatus', 'flatReitElementListByStatus', 'ratingAllocations'];
			return getReitElementsForInvestmentEntity(dashboardFormatTypes);
		}

		function getReitElementListsForPlan(riaPlanId) {
			var dashboardFormatTypes = ['weightedReitElementsByStatus', 'flatReitElementListByStatus', 'ratingAllocations'];
			return getReitElementsForRiaPlan(riaPlanId, dashboardFormatTypes);
		}

		function formatReitElement(reitElement) {
			PropertyService.formatPropertyListItem(reitElement);
			reitElement.returnSort = getReturnSort(reitElement);
			reitElement.riskScore = parseInt(reitElement.riskScore);
			return reitElement;
		}

		function getFormattedReitElementLists(weightedReitElements, formats) {
			var weightedElements =  weightedReitElements.map(function(weightedReitElement) {
				formatReitElement(weightedReitElement.reitElement);
				return weightedReitElement;
			});

			var formattedReitElementLists = {};

			angular.forEach(formats, function(format) {
				formattedReitElementLists[format] = formattingFunctions[format](weightedElements);
			});

			return formattedReitElementLists;
		}

		function groupReitElementsByStatus(reitElements) {
			return {
				'active': $filter('filter')(reitElements, {status: 'ACTIVE'}, true),
				'redeemed': $filter('filter')(reitElements, {status: 'REDEEMED'}, true)
			};
		}

		function getWeightedReitElementsByStatus(weightedReitElements) {
			return groupReitElementsByStatus(getFlatReitElementList(weightedReitElements, true));
		}

		function getFlatReitElementList(weightedReitElements, withWeights) {
			return weightedReitElements.map(function(weightedReitElement) {
				if(withWeights) {
					weightedReitElement.reitElement.weightedAllocation = Number(Big(weightedReitElement.weightedAllocation));
				}
				return weightedReitElement.reitElement;
			});
		}

		function getFlatReitElementListByStatus(weightedReitElements) {
			return groupReitElementsByStatus(getFlatReitElementList(weightedReitElements));
		}

		function getFlatReitElementListWithWeights(weightedReitElements) {
			return getFlatReitElementList(weightedReitElements, true);
		}

		function getFlatReitElementListByStatusWithWeights(weightedReitElements) {
			return groupReitElementsByStatus(getFlatReitElementList(weightedReitElements, true));
		}

		function getReitElementsByRatingAllocation(weightedReitElements) {
			var ratingAllocations = weightedReitElements.reduce(function(ratingAllocations, weightedReitElement) {
				if(weightedReitElement.reitElement.status === 'ACTIVE') {
					var existingRatingAllocation = $filter('filter')(ratingAllocations, { 'letter': weightedReitElement.reitElement.rating.letter, 'subletter': weightedReitElement.reitElement.rating.subletter });
					if(existingRatingAllocation.length === 1) {
						weightedReitElement.reitElement.weightedAllocation = Number(Big(weightedReitElement.weightedAllocation));
						existingRatingAllocation[0].properties.push(weightedReitElement.reitElement);
						var bigAggregateWeighted = Big(existingRatingAllocation[0].aggregateWeightedAllocation).plus(Big(weightedReitElement.weightedAllocation));
						existingRatingAllocation[0].aggregateWeightedAllocation = Number(bigAggregateWeighted);
					} else {
						weightedReitElement.reitElement.weightedAllocation = Number(Big(weightedReitElement.weightedAllocation));
						var ratingAllocation = {
							'aggregateWeightedAllocation': Number(Big(weightedReitElement.weightedAllocation)),
							'riskScore': Number(weightedReitElement.reitElement.rating.riskScore),
							'color': weightedReitElement.reitElement.rating.color,
							'letter': weightedReitElement.reitElement.rating.letter,
							'subletter': weightedReitElement.reitElement.rating.subletter,
							'properties': [weightedReitElement.reitElement]
						};
						ratingAllocations.push(ratingAllocation);
					}
				}
				return ratingAllocations;
			}, []);

			return $filter('orderBy')(ratingAllocations, 'riskScore');
		}

		function getReturnSort(reitElement) {
			var bigLowerBound = Big(reitElement.projectedReturnLowerBound);
			var bigUpperBound = Big(reitElement.projectedReturnUpperBound);
			var bigAnnualInterest = Big(reitElement.annualInterestRate);

			if(!bigLowerBound.eq(0) && !bigUpperBound.eq(0)) {
				return Number(bigLowerBound);
			} else if (!bigAnnualInterest.eq(0)) {
				return Number(bigAnnualInterest);
			} else {
				return -999;
			}
		}
	}
})(window.angular);